import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowLeft } from "lucide-react";

function renderContent(text) {
  return text.split("\n").filter(Boolean).map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="font-heading text-2xl font-bold mt-8 mb-3">{line.replace("## ", "")}</h2>;
    if (line.startsWith("- ")) return <li key={i} className="ml-5 list-disc text-foreground/80">{line.replace("- ", "")}</li>;
    return <p key={i} className="text-foreground/80 leading-relaxed mb-4">{line}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const { t, lang } = useLang();
  const pick = (o, f) => (lang === "es" && o && o[`${f}_es`]) || (o ? o[f] : "");
  const [post, setPost] = useState(null);

  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false)); }, [slug]);

  if (post === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-foreground">{t("blog.notFound")} <Link to="/blog" className="text-primary underline">{t("blog.backToGuides")}</Link></div>;
  if (!post) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">{t("common.loading")}</div>;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-blog"><ArrowLeft className="w-4 h-4" /> {t("blog.allGuides")}</Link>
      <div className="flex gap-2 mb-4">{post.tags?.map((tg) => <span key={tg} className="text-xs bg-primary/5 text-primary rounded-full px-2.5 py-0.5">{tg}</span>)}</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold leading-tight">{pick(post, "title")}</h1>
      <p className="text-sm text-muted-foreground mt-3">{t("blog.by", { author: post.author })}</p>
      <img src={post.cover_image} alt={post.title} className="rounded-2xl w-full aspect-[16/8] object-cover my-8" />
      <div className="prose-content">{renderContent(pick(post, "content"))}</div>

      <div className="rounded-2xl bg-primary/5 border border-primary/10 p-8 mt-12 text-center">
        <h3 className="font-heading text-xl font-bold">{t("blog.ctaTitle")}</h3>
        <p className="text-muted-foreground mt-2">{t("blog.ctaSub")}</p>
        <Button className="rounded-full mt-4 px-6" onClick={() => openQuote({ type: "quote" })} data-testid="blogpost-quote-btn">{t("common.getFreeQuote")}</Button>
      </div>
    </article>
  );
}
